import { useEffect, useState } from 'react';
import { usePublicClient, useWalletClient } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { environment } from '@/config/environment';
import { TEAM_TOKEN_DISTRIBUTION_ABI } from '@/contracts/TeamTokenDistribution.abi';
import type { Address } from 'viem';

export interface VestingGrant {
  id: bigint;
  totalAmount: bigint;
  grantDate: bigint;
  releasedAmount: bigint;
  useCustomSchedule: boolean;
  isActive: boolean;
  releasableAmount?: bigint;
}

export interface EmploymentInfo {
  employmentDate: bigint;
  isTerminated: boolean;
  terminationDate: bigint;
}

export interface CustomSchedule {
  dates: bigint[];
  amounts: bigint[];
}

export interface VestingScheduleItem {
  date: Date;
  percentage: number;
  amount: bigint;
  unlocked: boolean;
}

// Contract duration constants (in seconds)
interface ContractDurations {
  cliffDuration: bigint;
  vestingDuration: bigint;
  monthlyDuration: bigint;
}

export function useTeamDistribution() {
  const { address, isConnected } = useAppKitAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const [employmentInfo, setEmploymentInfo] = useState<EmploymentInfo | null>(null);
  const [grants, setGrants] = useState<VestingGrant[]>([]);
  const [grantIds, setGrantIds] = useState<bigint[]>([]);
  const [isEligible, setIsEligible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [tgeDate, setTgeDate] = useState<bigint | null>(null);
  const [totalReleasable, setTotalReleasable] = useState<bigint>(BigInt(0));
  const [durations, setDurations] = useState<ContractDurations | null>(null);

  const contractAddress = environment.distributionAddress as Address;

  // Fetch contract duration constants
  useEffect(() => {
    if (!publicClient) {
      setDurations(null);
      return;
    }

    const fetchDurations = async () => {
      try {
        const [cliffDuration, vestingDuration, monthlyDuration] = await Promise.all([
          publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'CLIFF_DURATION',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'VESTING_DURATION',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'MONTHLY_DURATION',
          }) as Promise<bigint>,
        ]);

        setDurations({
          cliffDuration,
          vestingDuration,
          monthlyDuration,
        });
      } catch (err) {
        console.error('Error fetching duration constants:', err);
      }
    };

    fetchDurations();
  }, [publicClient, contractAddress]);

  // Fetch TGE date
  useEffect(() => {
    if (!publicClient) {
      setTgeDate(null);
      return;
    }

    const fetchTGEDate = async () => {
      try {
        const date = await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'tgeDate',
        }) as bigint;

        setTgeDate(date);
      } catch (err) {
        console.error('Error fetching TGE date:', err);
      }
    };

    fetchTGEDate();
  }, [publicClient, contractAddress]);

  // Check if user is beneficiary and fetch their data
  useEffect(() => {
    if (!isConnected || !address || !publicClient) {
      setIsEligible(false);
      setEmploymentInfo(null);
      setGrants([]);
      setGrantIds([]);
      return;
    }

    const fetchBeneficiaryData = async () => {
      setIsLoading(true);
      try {
        // Check if address is a beneficiary
        const isBenef = await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'isBeneficiary',
          args: [address as Address],
        }) as boolean;

        setIsEligible(isBenef);

        if (isBenef) {
          // Fetch employment info
          const [employmentDate, isTerminated, terminationDate] = await publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'getEmploymentInfo',
            args: [address as Address],
          }) as [bigint, boolean, bigint];

          setEmploymentInfo({
            employmentDate,
            isTerminated,
            terminationDate,
          });

          // Fetch grant IDs
          const grantIdArray = await publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'getBeneficiaryGrants',
            args: [address as Address],
          }) as bigint[];

          setGrantIds(grantIdArray);

          // Fetch all grants
          const grantPromises = grantIdArray.map(async (grantId) => {
            const grant = await publicClient.readContract({
                address: contractAddress,
                abi: TEAM_TOKEN_DISTRIBUTION_ABI,
                functionName: 'getVestingGrant',
                args: [grantId],
              }) as { id: bigint, beneficiaryAddress: string, totalAmount: bigint, grantDate: bigint, firstVestingDate: bigint, releasedAmount: bigint, customDates: bigint[], customAmounts: bigint[], useCustomSchedule: boolean, isActive: boolean };

            // Fetch releasable amount for this grant
            const releasableAmount = await publicClient.readContract({
              address: contractAddress,
              abi: TEAM_TOKEN_DISTRIBUTION_ABI,
              functionName: 'releasableAmountForGrant',
              args: [address as Address, grantId],
            }) as bigint;

            console.log(`Grant ${grantId}: releasableAmount =`, releasableAmount.toString());

            return {
              id: grant.id,
              totalAmount: grant.totalAmount,
              grantDate: grant.grantDate,
              releasedAmount: grant.releasedAmount,
              useCustomSchedule: grant.useCustomSchedule,
              isActive: grant.isActive,
              releasableAmount,
            };
          });

          const fetchedGrants = await Promise.all(grantPromises);
          setGrants(fetchedGrants);

          // Fetch total releasable amount
          const releasable = await publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'releasableAmount',
            args: [address as Address],
          }) as bigint;

          setTotalReleasable(releasable);
        }

        // Check if user is owner
        const owner = await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'owner',
        }) as Address;

        setIsOwner(owner.toLowerCase() === address.toLowerCase());
      } catch (err) {
        console.error('Error fetching beneficiary data:', err);
        setError('Failed to fetch vesting information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBeneficiaryData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchBeneficiaryData, 30000);
    return () => clearInterval(interval);
  }, [isConnected, address, publicClient, contractAddress]);

  const releaseTokens = async () => {
    if (!address || !walletClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'release',
        args: [address as Address],
      });

      // Wait for transaction confirmation
      await publicClient?.waitForTransactionReceipt({ hash });

      // Refresh data after release
      if (publicClient && address) {
        const releasable = await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'releasableAmount',
          args: [address as Address],
        }) as bigint;

        setTotalReleasable(releasable);

        // Refresh grants
        const grantPromises = grantIds.map(async (grantId) => {
          const grant = await publicClient.readContract({
              address: contractAddress,
              abi: TEAM_TOKEN_DISTRIBUTION_ABI,
              functionName: 'getVestingGrant',
              args: [grantId],
            }) as { id: bigint, beneficiaryAddress: string, totalAmount: bigint, grantDate: bigint, firstVestingDate: bigint, releasedAmount: bigint, customDates: bigint[], customAmounts: bigint[], useCustomSchedule: boolean, isActive: boolean };

          return {
            id: grant.id,
            totalAmount: grant.totalAmount,
            grantDate: grant.grantDate,
            releasedAmount: grant.releasedAmount,
            useCustomSchedule: grant.useCustomSchedule,
            isActive: grant.isActive,
          };
        });

        const fetchedGrants = await Promise.all(grantPromises);
        setGrants(fetchedGrants);
      }
    } catch (err: any) {
      console.error('Error releasing tokens:', err);
      setError(err.message || 'Failed to release tokens');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const releaseFromGrant = async (grantId: bigint) => {
    if (!address || !walletClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'claimFromGrant',
        args: [grantId],
      });

      // Wait for transaction confirmation
      await publicClient?.waitForTransactionReceipt({ hash });

      // Refresh data after release
      if (publicClient && address) {
        const releasable = await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'releasableAmount',
          args: [address as Address],
        }) as bigint;

        setTotalReleasable(releasable);

        // Refresh grants
        const grantPromises = grantIds.map(async (gid) => {
          const grant = await publicClient.readContract({
              address: contractAddress,
              abi: TEAM_TOKEN_DISTRIBUTION_ABI,
              functionName: 'getVestingGrant',
              args: [gid],
            }) as { id: bigint, beneficiaryAddress: string, totalAmount: bigint, grantDate: bigint, firstVestingDate: bigint, releasedAmount: bigint, customDates: bigint[], customAmounts: bigint[], useCustomSchedule: boolean, isActive: boolean };

          // Fetch releasable amount for this grant
          const releasableAmount = await publicClient.readContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'releasableAmountForGrant',
            args: [address as Address, gid],
          }) as bigint;

          console.log(`Refresh Grant ${gid}: releasableAmount =`, releasableAmount.toString());

          return {
            id: grant.id,
            totalAmount: grant.totalAmount,
            grantDate: grant.grantDate,
            releasedAmount: grant.releasedAmount,
            useCustomSchedule: grant.useCustomSchedule,
            isActive: grant.isActive,
            releasableAmount,
          };
        });

        const fetchedGrants = await Promise.all(grantPromises);
        setGrants(fetchedGrants);
      }
    } catch (err: any) {
      console.error('Error releasing tokens from grant:', err);
      setError(err.message || 'Failed to release tokens');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getCustomSchedule = async (grantId: bigint): Promise<CustomSchedule | null> => {
    if (!address || !publicClient) {
      return null;
    }

    try {
      const [dates, amounts] = await publicClient.readContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'getCustomSchedule',
        args: [address as Address, grantId],
      }) as [bigint[], bigint[]];

      return { dates, amounts };
    } catch (err) {
      console.error('Error fetching custom schedule:', err);
      return null;
    }
  };

  const getGrantById = async (grantId: bigint) => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    try {
      const [beneficiary, id, totalAmount, grantDate, firstVestingDate, releasedAmount, useCustomSchedule, isActive] =
        await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'getGrantById',
          args: [grantId],
        }) as [Address, bigint, bigint, bigint, bigint, bigint, boolean, boolean];

      return {
        beneficiary,
        id,
        totalAmount,
        grantDate,
        firstVestingDate,
        releasedAmount,
        useCustomSchedule,
        isActive,
      };
    } catch (err) {
      console.error('Error fetching grant by ID:', err);
      throw err;
    }
  };

  const getCustomScheduleById = async (grantId: bigint): Promise<CustomSchedule | null> => {
    if (!publicClient) {
      return null;
    }

    try {
      const [dates, amounts] = await publicClient.readContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'getCustomScheduleById',
        args: [grantId],
      }) as [bigint[], bigint[]];

      return { dates, amounts };
    } catch (err) {
      console.error('Error fetching custom schedule by ID:', err);
      return null;
    }
  };

  const getMaxGrantId = async (): Promise<bigint> => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    try {
      const nextId = await publicClient.readContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'nextGrantId',
      }) as bigint;

      return nextId - BigInt(1); // nextGrantId is the next ID to be used, so max is nextId - 1
    } catch (err) {
      console.error('Error fetching max grant ID:', err);
      throw err;
    }
  };

  const calculateVestingSchedule = (grant: VestingGrant): VestingScheduleItem[] => {
    if (!employmentInfo || !durations || !tgeDate) return [];

    // For custom schedules, we need to fetch the custom dates/amounts
    // This is a simplified version that works for default schedules
    if (grant.useCustomSchedule) {
      // Custom schedules need to be handled separately
      return [];
    }

    // Use contract values (in seconds)
    const cliffDurationSeconds = Number(durations.cliffDuration);
    const vestingDurationSeconds = Number(durations.vestingDuration);
    const monthlyDurationSeconds = Number(durations.monthlyDuration);

    // Calculate total months from contract durations
    const totalVestingAfterCliff = vestingDurationSeconds - cliffDurationSeconds;
    const totalMonths = Math.floor(totalVestingAfterCliff / monthlyDurationSeconds);

    const CLIFF_PERCENTAGE = 25;
    const MONTHLY_PERCENTAGE = 75 / totalMonths;

    // Use TGE date as the unlock start time
    const startTime = Number(tgeDate);
    const totalAmount = grant.totalAmount;
    const now = Math.floor(Date.now() / 1000);

    const schedule: VestingScheduleItem[] = [];

    // Cliff at 1 year - 25% unlock
    const cliffTime = startTime + cliffDurationSeconds;
    const cliffAmount = totalAmount / BigInt(4);
    schedule.push({
      date: new Date(cliffTime * 1000),
      percentage: CLIFF_PERCENTAGE,
      amount: cliffAmount,
      unlocked: now >= cliffTime,
    });

    // Remaining 75% vests monthly over totalMonths
    const monthlyAmount = (totalAmount * BigInt(75)) / BigInt(100) / BigInt(totalMonths);

    for (let i = 1; i <= totalMonths; i++) {
      const unlockTime = cliffTime + (i * monthlyDurationSeconds);
      schedule.push({
        date: new Date(unlockTime * 1000),
        percentage: MONTHLY_PERCENTAGE,
        amount: monthlyAmount,
        unlocked: now >= unlockTime,
      });
    }

    return schedule;
  };

  const getTotalAllocation = (): bigint => {
    return grants.reduce((total, grant) => {
      if (grant.isActive) {
        return total + grant.totalAmount;
      }
      return total;
    }, BigInt(0));
  };

  const getTotalReleased = (): bigint => {
    return grants.reduce((total, grant) => {
      if (grant.isActive) {
        return total + grant.releasedAmount;
      }
      return total;
    }, BigInt(0));
  };

  return {
    // State
    employmentInfo,
    grants,
    grantIds,
    isEligible,
    isLoading,
    error,
    isOwner,
    tgeDate,
    totalReleasable,
    durations,

    // Computed values
    totalAllocation: getTotalAllocation(),
    totalReleased: getTotalReleased(),

    // Actions
    releaseTokens,
    releaseFromGrant,
    getCustomSchedule,
    getGrantById,
    getCustomScheduleById,
    getMaxGrantId,
    calculateVestingSchedule,
    setError,
  };
}
